import React, { useEffect, useState, useMemo } from "react";
import Chart from "react-apexcharts";
import jsonData from "../../../../current-survey-numeric.json"; // Ensure the path is correct

const TopLeftChart = ({ filterCriteria }) => {
  const [chartInstance, setChartInstance] = useState(null);
  const [filteredScores, setFilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [unfilteredScores, setUnfilteredScores] = useState({
    NR: 0,
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0,
  });

  const [showMessage, setShowMessage] = useState(false);
  const [chartHeight, setChartHeight] = useState("300px");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
      
      // Adjust chart height based on window width
      if (window.innerWidth < 768) {
        setChartHeight("250px");
      } else if (window.innerWidth < 1200) {
        setChartHeight("280px");
      } else {
        setChartHeight("300px");
      }
      
      // Force chart to redraw after resize if we have a chart instance
      if (chartInstance) {
        setTimeout(() => {
          try {
            chartInstance.render();
          } catch (error) {
            console.log("Error rendering chart:", error);
          }
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Set initial size
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartInstance]);

  useEffect(() => {
    const processScores = (data) => {
      const scores = {
        NR: 0,
        1: 0,
        2: 0,
        3: 0,
        4: 0,
        5: 0,
      };

      data.forEach((item) => {
        const score = item.MQ0009;
        if ([1, 2, 3, 4, 5].includes(score)) {
          scores[score]++;
        } else {
          scores.NR++;
        }
      });

      return scores;
    };

    // Filter the data based on the provided filterCriteria
    const filteredData = jsonData.filter((item) => {
      return Object.keys(filterCriteria).every(
        (key) =>
          filterCriteria[key].length === 0 ||
          filterCriteria[key].includes(item[key])
      );
    });

    // Show message if there are fewer than 4 respondents
    setShowMessage(filteredData.length < 4);

    // Process the data if there are enough respondents
    if (filteredData.length >= 4) {
      const newFilteredScores = processScores(filteredData);
      const newUnfilteredScores = processScores(jsonData);

      setFilteredScores(newFilteredScores);
      setUnfilteredScores(newUnfilteredScores);
    }
  }, [filterCriteria]);

  const labelToScoreKeyMap = {
    "Intensely Dislike": 1,
    "Do Not Like": 2,
    "Neither Like or Dislike": 3,
    "Like my work": 4,
    "Love my work": 5,
    "NR": "NR",
  };

  // Calculate totals for percentages
  const totalFiltered = Object.values(filteredScores).reduce(
    (acc, curr) => acc + curr,
    0
  );
  const totalUnfiltered = Object.values(unfilteredScores).reduce(
    (acc, curr) => acc + curr,
    0
  );

  const seriesData = Object.keys(filteredScores).map((key) => {
    const filteredPercentage =
      totalFiltered > 0 ? (filteredScores[key] / totalFiltered) * 100 : 0;
    const unfilteredPercentage =
      totalUnfiltered > 0 ? (unfilteredScores[key] / totalUnfiltered) * 100 : 0;

    return {
      x: key,
      y: filteredPercentage,
      goals: [
        {
          name: "Unfiltered",
          value: unfilteredPercentage,
          strokeHeight: 12,
          strokeColor: "#6D6D6D",
        },
      ],
    };
  });

  const options = useMemo(
    () => ({
      chart: {
        type: "bar",
        height: chartHeight,
        toolbar: {
          show: false
        },
        redrawOnWindowResize: true,
        redrawOnParentResize: true,
        animations: {
          enabled: false
        },
      },
      colors: [
        "#F28E2B",
        "#F28E2B",
        "#F1CE63",
        "#499894",
        "#499894",
        "#BAB0AC",
      ],
      plotOptions: {
        bar: {
          borderRadius: 4,
          borderRadiusApplication: "end",
          horizontal: true,
          distributed: true,
          dataLabels: {
            position: "top",
          },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: function (val, opt) {
          const xAxisLabel = opt.w.globals.labels[opt.dataPointIndex];
          const scoreKey = labelToScoreKeyMap[xAxisLabel];
          const filteredValue = filteredScores[scoreKey] || 0;
          const unfilteredValue = unfilteredScores[scoreKey] || 0;

          const filteredPercentage =
            totalFiltered > 0
              ? ((filteredValue / totalFiltered) * 100).toFixed(1)
              : "0.0";
          const unfilteredPercentage =
            totalUnfiltered > 0
              ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
              : "0.0";

          // For smaller screens, show shorter labels
          return windowWidth < 768 ? 
            `${filteredPercentage}%` : 
            `${filteredPercentage}% | ${unfilteredPercentage}%`;
        },
        style: {
          colors: ["#000"],
          fontSize: windowWidth < 768 ? '10px' : '12px',
        },
        offsetX: windowWidth < 768 ? 35 : 65,
      },
      xaxis: {
        categories: [
          "Intensely Dislike",
          "Do Not Like",
          "Neither Like or Dislike",
          "Like my work",
          "Love my work",
          "NR",
        ],
        title: {
          text: "% of Responses",
          style: {
            fontSize: windowWidth < 768 ? "10px" : "12px",
            fontWeight: "normal",
            color: "#000",
          },
        },
        labels: {
          style: {
            fontSize: windowWidth < 768 ? '9px' : '11px',
          },
        },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        custom: function ({ series, seriesIndex, dataPointIndex, w }) {
          const xAxisLabel = w.globals.labels[dataPointIndex];
          const scoreKey = labelToScoreKeyMap[xAxisLabel];
          const filteredValue = filteredScores[scoreKey] || 0;
          const unfilteredValue = unfilteredScores[scoreKey] || 0;

          const filteredPercentage =
            totalFiltered > 0
              ? ((filteredValue / totalFiltered) * 100).toFixed(1)
              : "0.0";
          const unfilteredPercentage =
            totalUnfiltered > 0
              ? ((unfilteredValue / totalUnfiltered) * 100).toFixed(1)
              : "0.0";

          return `<div class="apexcharts-tooltip-text">
                    <strong>${xAxisLabel}</strong>: ${filteredPercentage}% | ${unfilteredPercentage}%
                  </div>`;
        },
      },
      yaxis: {
        max: 100,
        labels: {
          formatter: (val) =>
            typeof val === "number" ? `${val.toFixed(0)}` : val,
          style: {
            fontSize: windowWidth < 768 ? '9px' : '11px',
          },
        },
      },
      legend: {
        show: false,
      },
      responsive: [
        {
          breakpoint: 768,
          options: {
            chart: {
              height: '250px'
            },
            plotOptions: {
              bar: {
                horizontal: true,
              }
            }
          }
        },
        {
          breakpoint: 480,
          options: {
            chart: {
              height: '220px'
            },
          }
        }
      ]
    }),
    [filteredScores, unfilteredScores, totalFiltered, totalUnfiltered, chartHeight, windowWidth]
  );

  return (
    <div className="chart-container" style={{ position: 'relative', width: '100%', height: '100%' }}>
      {showMessage && (
        <div style={{ color: "red", marginTop: "20px", textAlign: "center" }}>
          Current filter selections are returning fewer than the minimum number
          of respondents. Please expand the selection.
        </div>
      )}
      {!showMessage && (
        <div id="d08-top-left" style={{ width: '100%', height: '100%' }}>
          <Chart
            key={`chart-${windowWidth}-${JSON.stringify(seriesData)}`} // Ensure redraw on changes
            options={options}
            series={[{ name: "Filtered", data: seriesData }]}
            type="bar"
            height={chartHeight}
            width="100%"
            onMount={(chart) => {
              setChartInstance(chart);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default TopLeftChart;